/**
 * Reusable print templates — invoice, KOT, delivery challan.
 *
 * Each `printX(...)` opens an A4 / A5 / 80mm window with the right
 * letterhead, GST split (CGST/SGST/IGST), customer block, line table,
 * totals + amount-in-words. The browser's print dialog is fired
 * automatically; users can save as PDF via the OS dialog.
 *
 * No client-side PDF library — keeps the bundle small. The HTML is
 * print-style-tuned with @page rules.
 */

import { getInvoiceColumns } from './invoiceColumns';
// qrSvgDataUri is a runtime import; branding.ts only imports a TYPE from here,
// so there's no runtime circular dependency.
import { qrSvgDataUri } from './branding';

export interface TenantHeader {
  name: string;
  /** Company logo (hosted URL or data URI) shown in the print header. */
  logoUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface PartyBlock {
  /** "Bill to" / "Ship to" / "Supplier" / "Vendor" / etc. */
  label: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstin?: string;
  phone?: string;
  email?: string;
}

export interface InvoiceLine {
  description: string;
  remarks?: string;
  hsnCode?: string;
  qty: number;
  unit?: string;
  noOfPacks?: number;
  packType?: string;
  rate: number;
  discountPct?: number;
  tradeDiscount?: number;
  taxRate: number;
  /** pre-tax taxable value of the line (qty × rate − discount). */
  taxable?: number;
  /** taxable + tax — pre-computed by the caller. */
  total: number;
}

export interface InvoiceBank {
  name?: string;
  accountName?: string;
  accountNo?: string;
  ifsc?: string;
  branch?: string;
}

export interface HsnSummaryRow {
  hsn: string;
  taxable: number;
  cgstRate: number; cgst: number;
  sgstRate: number; sgst: number;
  igstRate: number; igst: number;
}

export interface InvoicePrintInput {
  docType: 'TAX INVOICE' | 'INVOICE' | 'CREDIT NOTE' | 'DEBIT NOTE' | 'PROFORMA' | 'QUOTATION' | 'PURCHASE INVOICE';
  number: string;
  date: string;
  dueDate?: string;
  /** "B2B" → show GSTIN block, "B2C" → omit */
  customerType?: 'B2B' | 'B2C' | string;
  tenant: TenantHeader;
  parties: PartyBlock[];          // typically [billTo] or [billTo, shipTo]
  lines: InvoiceLine[];
  /** Totals already split client-side. */
  subTotal: number;
  totalDiscount: number;
  taxableAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  roundOff: number;
  totalAmount: number;
  notes?: string;
  terms?: string;
  /** e-Invoice IRN (when generated). Renders as a QR alongside the totals. */
  irn?: string;
  ackNo?: string;
  ackDate?: string;
  /** e-Way Bill no. (when present). */
  ewayBillNo?: string;
  /** Extra header fields shown on the detailed tax invoice. */
  placeOfSupply?: string;
  panNumber?: string;
  challanNumber?: string;
  paymentTerms?: string;
  dueDate2?: string;
  freightCharges?: number;
  /** Per-HSN Central/State tax summary table. */
  hsnSummary?: HsnSummaryRow[];
  /** Admin-uploadable branding (signature image, QR, bank details). */
  bank?: InvoiceBank;
  signatureUrl?: string;
  qrUrl?: string;
  /** Bill-level coupon, shown as its own discount line on the print. */
  couponDiscount?: number;
  couponCode?: string;
}

export interface KotPrintInput {
  tableLabel: string;
  orderType: 'DineIn' | 'Takeaway' | 'Delivery' | string;
  guests?: number;
  customerName?: string;
  kotNumber?: string;
  kotTime?: string;
  items: { name: string; qty: number; notes?: string }[];
  tenant: TenantHeader;
}

export interface DeliveryChallanInput {
  number: string;
  date: string;
  vehicleNumber?: string;
  driver?: string;
  /** purpose: SaleOnApproval / JobWork / SuppliedToBranch / Others */
  purpose?: string;
  tenant: TenantHeader;
  parties: PartyBlock[]; // typically [consignor, consignee]
  lines: { description: string; hsnCode?: string; qty: number; unit?: string }[];
  notes?: string;
}

/* -------------------------------------------------------------------- */
/*  Public entry points                                                 */
/* -------------------------------------------------------------------- */

export function printInvoice(input: InvoicePrintInput): void {
  openWindow(invoiceHtml(input), 'A4');
}

export function printKot(input: KotPrintInput): void {
  openWindow(kotHtml(input), '80mm');
}

export function printDeliveryChallan(input: DeliveryChallanInput): void {
  openWindow(deliveryChallanHtml(input), 'A4');
}

/** Same invoice data, rendered on an 80mm thermal receipt. */
export function printThermalInvoice(input: InvoicePrintInput): void {
  openWindow(thermalInvoiceHtml(input), '80mm');
}

/**
 * Render invoice-shaped data using the admin-assigned print FORMAT.
 *   'a4-tax-invoice' / 'a4-purchase-invoice' → A4 invoice (docType set by caller)
 *   'thermal-80'                              → 80mm receipt
 * KOT / delivery-challan have their own dedicated entry points.
 */
export function printInvoiceByFormat(
  format: 'a4-tax-invoice' | 'a4-purchase-invoice' | 'thermal-80' | string,
  input: InvoicePrintInput,
): void {
  if (format === 'thermal-80') printThermalInvoice(input);
  else if (format === 'a4-tax-invoice') openWindow(taxInvoiceHtml(input), 'A4');
  else printInvoice(input);
}

/* -------------------------------------------------------------------- */
/*  Window scaffolding + shared CSS                                     */
/* -------------------------------------------------------------------- */

function openWindow(body: string, page: 'A4' | '80mm'): void {
  const win = window.open('', '_blank', page === 'A4' ? 'width=900,height=1100' : 'width=320,height=700');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>KSS BillBook</title>
<style>${baseCss(page)}</style></head><body>${body}<script>window.onload=()=>window.print();</script></body></html>`);
  win.document.close();
}

function baseCss(page: 'A4' | '80mm'): string {
  if (page === '80mm') {
    return `
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: monospace; margin: 0; padding: 4mm; width: 72mm; color: #000; }
      h1 { font-size: 14px; margin: 0 0 4px; text-align: center; }
      .meta { font-size: 10px; color: #444; text-align: center; margin-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { padding: 2px 0; }
      thead { border-bottom: 1px dashed #888; }
      tfoot { border-top: 1px dashed #888; }
      .right { text-align: right; }
      .footnote { margin-top: 8px; font-size: 10px; text-align: center; color: #444; }
    `;
  }
  return `
    /* Tight left/right margins so the invoice uses the full page width
       (user request: minimal side margins). Keep a small top/bottom so
       printers don't clip headers/footers. */
    @page { size: A4; margin: 8mm 2mm; }
    * { box-sizing: border-box; }
    body { font-family: ui-sans-serif, system-ui, sans-serif; margin: 0; color: #0f172a; }
    .doc { padding: 4mm 3mm; max-width: 100%; margin: 0 auto; }
    .row { display: flex; gap: 16px; align-items: flex-start; }
    .right { text-align: right; }
    .muted { color: #64748b; }
    h1 { font-size: 22px; margin: 0; }
    h2 { font-size: 13px; margin: 0 0 4px; text-transform: uppercase; letter-spacing: .04em; color: #475569; }
    .doctype { font-size: 13px; font-weight: 700; letter-spacing: .12em; color: #1e3a8a; text-transform: uppercase; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #1e3a8a; }
    .meta-list { font-size: 11px; color: #475569; }
    .meta-list b { color: #0f172a; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0; }
    .party-card { padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 4px; font-size: 11px; }
    .party-card h3 { margin: 0 0 4px; font-size: 11px; color: #1e3a8a; text-transform: uppercase; letter-spacing: .04em; }
    table.lines { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 11.5px; }
    table.lines th, table.lines td { padding: 3px 6px; border: 1px solid #cbd5e1; }
    table.lines thead th { background: #f1f5f9; text-align: left; }
    .totals { margin-top: 8px; display: flex; justify-content: flex-end; }
    .totals table { font-size: 12px; min-width: 280px; border-collapse: collapse; }
    .totals td { padding: 4px 8px; }
    .totals tr.line td { border-top: 1px solid #e2e8f0; }
    .totals tr.grand td { border-top: 2px solid #1e3a8a; font-weight: 700; font-size: 14px; padding-top: 6px; }
    .words { margin-top: 8px; font-size: 11px; color: #475569; }
    .words b { color: #0f172a; }
    .terms { margin-top: 14px; font-size: 10px; color: #475569; line-height: 1.5; }
    .footer { margin-top: 18px; display: flex; justify-content: space-between; font-size: 10px; color: #475569; }
    .signature { width: 180px; margin-top: 30px; border-top: 1px solid #94a3b8; padding-top: 4px; text-align: center; font-size: 10px; }
    .qr { width: 110px; height: 110px; }
    .badge { display: inline-block; padding: 2px 6px; font-size: 9px; font-weight: 700; border-radius: 3px; background: #fef3c7; color: #92400e; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
  `;
}

/* -------------------------------------------------------------------- */
/*  Invoice                                                             */
/* -------------------------------------------------------------------- */

function invoiceHtml(d: InvoicePrintInput): string {
  const isInterstate = d.igstAmount > 0;
  const showGstin = d.customerType !== 'B2C';
  const lineRows = d.lines
    .map((l, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${escapeHtml(l.description)}${l.hsnCode ? `<br><span class="muted">HSN ${escapeHtml(l.hsnCode)}</span>` : ''}</td>
        <td class="right">${l.qty}${l.unit ? ` ${escapeHtml(l.unit)}` : ''}</td>
        <td class="right">${inr(l.rate)}</td>
        <td class="right">${l.discountPct ? l.discountPct + '%' : '—'}</td>
        <td class="right">${l.taxRate}%</td>
        <td class="right">${inr(l.total)}</td>
      </tr>`)
    .join('');

  return `<div class="doc">
    <div class="header">
      <div>
        ${brandMarkHtml(d.tenant)}<h1>${escapeHtml(d.tenant.name)}</h1>
        <div class="meta-list">
          ${[d.tenant.address, d.tenant.city, d.tenant.state, d.tenant.pincode].filter(Boolean).map(escapeHtml).join(', ')}
          ${d.tenant.gstin ? `<br><b>GSTIN:</b> ${escapeHtml(d.tenant.gstin)}` : ''}
          ${d.tenant.pan ? ` · <b>PAN:</b> ${escapeHtml(d.tenant.pan)}` : ''}
          ${d.tenant.phone ? `<br>${escapeHtml(d.tenant.phone)}` : ''}
          ${d.tenant.email ? ` · ${escapeHtml(d.tenant.email)}` : ''}
        </div>
      </div>
      <div class="right">
        <div class="doctype">${d.docType}</div>
        <div class="meta-list">
          <b>${escapeHtml(d.number)}</b><br>
          Date: ${escapeHtml(fmtDate(d.date))}
          ${d.dueDate ? `<br>Due: ${escapeHtml(fmtDate(d.dueDate))}` : ''}
          ${d.ewayBillNo ? `<br>e-Way Bill: ${escapeHtml(d.ewayBillNo)}` : ''}
        </div>
      </div>
    </div>

    <div class="parties">
      ${d.parties.map((p) => partyCardHtml(p, showGstin)).join('')}
    </div>

    <table class="lines">
      <thead>
        <tr>
          <th style="width:30px">#</th>
          <th>Description</th>
          <th class="right" style="width:70px">Qty</th>
          <th class="right" style="width:80px">Rate</th>
          <th class="right" style="width:60px">Disc</th>
          <th class="right" style="width:50px">GST</th>
          <th class="right" style="width:90px">Amount</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>

    <div class="row" style="justify-content: space-between; margin-top: 8px;">
      <div style="flex: 1;">
        <div class="words">
          <b>Amount in words:</b> ${escapeHtml(numberToWordsInr(d.totalAmount))}
        </div>
        ${d.irn ? `
          <div style="margin-top: 8px;">
            <span class="badge">e-Invoice</span>
            <div class="meta-list" style="margin-top: 4px;"><b>IRN:</b> ${escapeHtml(d.irn)}</div>
            <img class="qr" alt="IRN QR" src="${qrSvgDataUri(d.irn)}" />
          </div>` : ''}
      </div>
      <div class="totals">
        <table>
          <tr class="line"><td>Subtotal</td><td class="right">₹ ${inr(d.subTotal)}</td></tr>
          ${d.totalDiscount > 0 ? `<tr><td>Discount</td><td class="right">− ₹ ${inr(d.totalDiscount)}</td></tr>` : ''}
          ${d.couponDiscount ? `<tr><td>Coupon${d.couponCode ? ` (${escapeHtml(d.couponCode)})` : ''}</td><td class="right">− ₹ ${inr(d.couponDiscount)}</td></tr>` : ''}
          <tr class="line"><td>Taxable</td><td class="right">₹ ${inr(d.taxableAmount)}</td></tr>
          ${isInterstate
            ? `<tr><td>IGST</td><td class="right">₹ ${inr(d.igstAmount)}</td></tr>`
            : `<tr><td>CGST</td><td class="right">₹ ${inr(d.cgstAmount)}</td></tr>
               <tr><td>SGST</td><td class="right">₹ ${inr(d.sgstAmount)}</td></tr>`}
          ${d.roundOff !== 0 ? `<tr><td>Round off</td><td class="right">${d.roundOff > 0 ? '+ ' : '− '}₹ ${inr(Math.abs(d.roundOff))}</td></tr>` : ''}
          <tr class="grand"><td>Grand total</td><td class="right">₹ ${inr(d.totalAmount)}</td></tr>
        </table>
      </div>
    </div>

    ${d.notes ? `<div class="terms"><b>Notes:</b> ${escapeHtml(d.notes)}</div>` : ''}
    ${d.terms ? `<div class="terms"><b>Terms &amp; conditions:</b><br>${escapeHtml(d.terms)}</div>` : ''}

    <div class="footer" style="align-items:flex-end">
      <div>Thank you for your business.</div>
      ${d.qrUrl ? `<div style="text-align:center"><img src="${escapeHtml(d.qrUrl)}" class="qr" alt="" onerror="this.style.display='none'"><div class="muted" style="font-size:9px">Scan to pay / verify</div></div>` : ''}
      <div class="right">
        For <b>${escapeHtml(d.tenant.name)}</b>
        ${d.signatureUrl ? `<div><img src="${escapeHtml(d.signatureUrl)}" alt="" onerror="this.style.display='none'" style="max-height:50px;margin-top:6px"></div>` : '<div style="height:36px"></div>'}
        <div class="signature">Authorised signatory</div>
      </div>
    </div>
  </div>`;
}

function partyCardHtml(p: PartyBlock, showGstin: boolean): string {
  return `<div class="party-card">
    <h3>${escapeHtml(p.label)}</h3>
    <div><b>${escapeHtml(p.name)}</b></div>
    ${[p.address, p.city, p.state, p.pincode].filter(Boolean).map(escapeHtml).join(', ')}
    ${showGstin && p.gstin ? `<br><b>GSTIN:</b> ${escapeHtml(p.gstin)}` : ''}
    ${p.phone ? `<br>${escapeHtml(p.phone)}` : ''}
    ${p.email ? ` · ${escapeHtml(p.email)}` : ''}
  </div>`;
}

/* -------------------------------------------------------------------- */
/*  KOT (80 mm thermal)                                                 */
/* -------------------------------------------------------------------- */

function kotHtml(d: KotPrintInput): string {
  const items = d.items
    .map(
      (i) => `<tr>
        <td>${escapeHtml(i.name)}${i.notes ? `<br><span style="color:#666;font-size:9px">${escapeHtml(i.notes)}</span>` : ''}</td>
        <td class="right">${i.qty}</td>
      </tr>`,
    )
    .join('');
  return `<h1>KOT — ${escapeHtml(d.tableLabel)}</h1>
    <div class="meta">${escapeHtml(d.orderType)}${
      d.guests ? ` · ${d.guests} guest${d.guests > 1 ? 's' : ''}` : ''
    }${d.customerName ? ` · ${escapeHtml(d.customerName)}` : ''}</div>
    <div class="meta">${escapeHtml(d.kotNumber ?? '')} · ${escapeHtml(d.kotTime ? fmtDateTime(d.kotTime) : new Date().toLocaleString('en-IN'))}</div>
    <table>
      <thead><tr><th align="left">Item</th><th class="right">Qty</th></tr></thead>
      <tbody>${items}</tbody>
    </table>
    <div class="footnote">${escapeHtml(d.tenant.name)}</div>`;
}

/* -------------------------------------------------------------------- */
/*  Invoice — 80mm thermal variant                                      */
/* -------------------------------------------------------------------- */

function thermalInvoiceHtml(d: InvoicePrintInput): string {
  const billTo = d.parties[0];
  const items = d.lines
    .map(
      (l) => `<tr>
        <td>${escapeHtml(l.description)}<br><span style="color:#666;font-size:9px">${l.qty}${l.unit ? ' ' + escapeHtml(l.unit) : ''} × ${inr(l.rate)}${l.taxRate ? ` · GST ${l.taxRate}%` : ''}</span></td>
        <td class="right">${inr(l.total)}</td>
      </tr>`,
    )
    .join('');
  const tax = d.cgstAmount + d.sgstAmount + d.igstAmount;
  return `${brandMarkHtml(d.tenant)}<h1>${escapeHtml(d.tenant.name)}</h1>
    <div class="meta">${[d.tenant.address, d.tenant.city].filter(Boolean).map(escapeHtml).join(', ')}${d.tenant.gstin ? `<br>GSTIN: ${escapeHtml(d.tenant.gstin)}` : ''}</div>
    <div class="meta">${escapeHtml(d.docType)} · ${escapeHtml(d.number)} · ${escapeHtml(fmtDate(d.date))}</div>
    ${billTo ? `<div class="meta">${escapeHtml(billTo.label)}: ${escapeHtml(billTo.name)}</div>` : ''}
    <table>
      <thead><tr><th align="left">Item</th><th class="right">Amount</th></tr></thead>
      <tbody>${items}</tbody>
      <tfoot>
        <tr><td>Subtotal</td><td class="right">${inr(d.subTotal)}</td></tr>
        ${d.totalDiscount > 0 ? `<tr><td>Discount</td><td class="right">- ${inr(d.totalDiscount)}</td></tr>` : ''}
        ${tax > 0 ? `<tr><td>GST</td><td class="right">${inr(tax)}</td></tr>` : ''}
        ${d.roundOff !== 0 ? `<tr><td>Round off</td><td class="right">${d.roundOff > 0 ? '+' : '-'} ${inr(Math.abs(d.roundOff))}</td></tr>` : ''}
        <tr><td><b>TOTAL</b></td><td class="right"><b>₹ ${inr(d.totalAmount)}</b></td></tr>
      </tfoot>
    </table>
    <div class="footnote">${escapeHtml(numberToWordsInr(d.totalAmount))}<br>Thank you · ${escapeHtml(d.tenant.name)}</div>`;
}

/* -------------------------------------------------------------------- */
/*  Delivery challan                                                    */
/* -------------------------------------------------------------------- */

function deliveryChallanHtml(d: DeliveryChallanInput): string {
  const lineRows = d.lines
    .map(
      (l, i) => `<tr>
        <td>${i + 1}</td>
        <td>${escapeHtml(l.description)}${l.hsnCode ? `<br><span class="muted">HSN ${escapeHtml(l.hsnCode)}</span>` : ''}</td>
        <td class="right">${l.qty}${l.unit ? ` ${escapeHtml(l.unit)}` : ''}</td>
      </tr>`,
    )
    .join('');
  return `<div class="doc">
    <div class="header">
      <div>
        ${brandMarkHtml(d.tenant)}<h1>${escapeHtml(d.tenant.name)}</h1>
        <div class="meta-list">
          ${[d.tenant.address, d.tenant.city, d.tenant.state, d.tenant.pincode].filter(Boolean).map(escapeHtml).join(', ')}
          ${d.tenant.gstin ? `<br><b>GSTIN:</b> ${escapeHtml(d.tenant.gstin)}` : ''}
        </div>
      </div>
      <div class="right">
        <div class="doctype">DELIVERY CHALLAN</div>
        <div class="meta-list">
          <b>${escapeHtml(d.number)}</b><br>
          Date: ${escapeHtml(fmtDate(d.date))}
          ${d.purpose ? `<br>Purpose: ${escapeHtml(d.purpose)}` : ''}
          ${d.vehicleNumber ? `<br>Vehicle: ${escapeHtml(d.vehicleNumber)}` : ''}
          ${d.driver ? `<br>Driver: ${escapeHtml(d.driver)}` : ''}
        </div>
      </div>
    </div>

    <div class="parties">
      ${d.parties.map((p) => partyCardHtml(p, true)).join('')}
    </div>

    <table class="lines">
      <thead>
        <tr>
          <th style="width:30px">#</th>
          <th>Description</th>
          <th class="right" style="width:90px">Qty</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>

    ${d.notes ? `<div class="terms"><b>Notes:</b> ${escapeHtml(d.notes)}</div>` : ''}

    <div class="footer">
      <div class="signature">Receiver&rsquo;s signature</div>
      <div class="signature">Authorised signatory</div>
    </div>
  </div>`;
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v: string): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(v: string): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function escapeHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/**
 * Company brand mark for the print header. Uses the uploaded logo image when
 * the tenant has one; otherwise renders a coloured monogram of the company
 * initials so EVERY print shows a visible brand mark (the tenant on the demo
 * has no logo uploaded — `logoUrl` is empty — so without this the header was
 * bare). The company name still prints in the adjacent <h1>.
 */
function brandMarkHtml(t: TenantHeader): string {
  if (t.logoUrl) {
    return `<img src="${escapeHtml(t.logoUrl)}" alt="" style="max-height:60px;max-width:180px;margin-bottom:6px;display:block;object-fit:contain" onerror="this.style.display='none'" />`;
  }
  return monogramHtml(t.name);
}

function monogramHtml(name: string): string {
  const initials = (name || 'KSS').trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'KS';
  // line-height vertical-centring (not flex) for print-engine reliability.
  return `<div style="width:54px;height:54px;border-radius:10px;background:#0f766e;color:#fff;font:700 22px/54px Arial,Helvetica,sans-serif;text-align:center;margin-bottom:6px">${escapeHtml(initials)}</div>`;
}

/**
 * Indian-style number-to-words. Splits the rupee + paise components and
 * uses the lakh/crore grouping (not the Western thousand/million one).
 */
function numberToWordsInr(amount: number): string {
  if (!Number.isFinite(amount)) return '';
  const rupees = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rupees) * 100);
  const main = rupees === 0 ? 'Zero' : indianWords(rupees);
  const tail = paise > 0 ? ` and ${indianWords(paise)} paise` : '';
  return `${amount < 0 ? 'Minus ' : ''}Rupees ${main}${tail} only`;
}

function indianWords(n: number): string {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
    'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function under1000(x: number): string {
    if (x === 0) return '';
    if (x < 20) return ones[x]!;
    if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '');
    return ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + under1000(x % 100) : '');
  }
  if (n < 1000) return under1000(n).trim();
  const crore = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thousand = Math.floor((n % 100_000) / 1000);
  const remainder = n % 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${under1000(crore).trim()} Crore`);
  if (lakh) parts.push(`${under1000(lakh).trim()} Lakh`);
  if (thousand) parts.push(`${under1000(thousand).trim()} Thousand`);
  if (remainder) parts.push(under1000(remainder).trim());
  return parts.join(' ');
}

/* -------------------------------------------------------------------- */
/*  Sales Order — detailed B2B GST layout (Bill-To / Ship-To, HSN        */
/*  table, tax breakdown, amount-in-words, bank + signatory).            */
/* -------------------------------------------------------------------- */

export interface SalesOrderPrintLine {
  description: string;
  hsnCode?: string;
  qty: number;
  unit?: string;
  rate: number;
  amount: number; // qty × rate (pre-tax)
}

export interface SalesOrderBank {
  accountName?: string;
  bankName?: string;
  accountNo?: string;
  ifsc?: string;
  branch?: string;
}

export interface SalesOrderPrintInput {
  /** Heading, e.g. "SALES ORDER" / "PURCHASE ORDER". Defaults to SALES ORDER. */
  docTitle?: string;
  number: string;
  date: string;
  buyerPoNumber?: string;
  buyerPoDate?: string;
  salesPerson?: string;
  dispatchThrough?: string;
  paymentTerms?: string;
  tenant: TenantHeader;
  billTo: PartyBlock;
  shipTo?: PartyBlock;
  lines: SalesOrderPrintLine[];
  subTotal: number;
  tradeDiscount: number;
  transportCharges: number;
  /** Pre-tax taxable value (subTotal − discount). Shown as its own row so the
   *  "Taxable" box appears below the line table, matching the tax invoice. */
  taxableAmount?: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  roundOff: number;
  totalAmount: number;
  narration?: string;
  transporter?: string;
  transportDetails?: string;
  bank?: SalesOrderBank;
  /** Admin-uploadable / auto-generated branding shared with the tax invoice. */
  signatureUrl?: string;
  qrUrl?: string;
}

export function printSalesOrder(input: SalesOrderPrintInput): void {
  openWindow(salesOrderHtml(input), 'A4');
}

export interface PaymentReceiptInput {
  docTitle?: string;        // 'PAYMENT RECEIPT' (default) / 'PURCHASE PAYMENT'
  number: string;
  date: string;
  method?: string;
  txnRef?: string;
  amount: number;
  currency?: string;
  tenant: TenantHeader;
  partyLabel?: string;      // 'Received From' / 'Paid To'
  partyName?: string;
  partyAddress?: string;
  partyPhone?: string;
  partyGstin?: string;
  againstLabel?: string;    // 'Against Invoice'
  againstNumber?: string;
  againstBalance?: number;
  status?: string;
  signatureUrl?: string;
  qrUrl?: string;
}

export function printPaymentReceipt(input: PaymentReceiptInput): void {
  openWindow(paymentReceiptHtml(input), 'A4');
}

function paymentReceiptHtml(d: PaymentReceiptInput): string {
  const row = (k: string, v: string) => v ? `<tr><td style="padding:4px 10px;color:#555;width:170px">${escapeHtml(k)}</td><td style="padding:4px 10px;font-weight:600">${escapeHtml(v)}</td></tr>` : '';
  return `<div class="doc" style="max-width:720px">
    <div class="header">
      <div>
        ${brandMarkHtml(d.tenant)}
        <h1>${escapeHtml(d.tenant.name)}</h1>
        <div class="meta-list">${[d.tenant.address, d.tenant.city, d.tenant.state, d.tenant.pincode].filter(Boolean).map(escapeHtml).join(', ')}</div>
        ${d.tenant.gstin ? `<div class="meta-list"><b>GSTIN:</b> ${escapeHtml(d.tenant.gstin)}</div>` : ''}
      </div>
      <div class="right"><div class="doctype">${escapeHtml(d.docTitle ?? 'PAYMENT RECEIPT')}</div></div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:12px;border:1px solid #e2e8f0">
      ${row('Receipt No', d.number)}
      ${row('Date', fmtDate(d.date))}
      ${row(d.partyLabel ?? 'Received From', d.partyName ?? '')}
      ${row('Address', d.partyAddress ?? '')}
      ${row('Phone', d.partyPhone ?? '')}
      ${row('GSTIN', d.partyGstin ?? '')}
      ${row(d.againstLabel ?? 'Against Invoice', d.againstNumber ?? '')}
      ${row('Payment Method', d.method ?? '')}
      ${row('Reference / Txn', d.txnRef ?? '')}
      ${row('Status', d.status ?? '')}
    </table>
    <div style="margin-top:14px;display:flex;justify-content:flex-end">
      <table style="border-collapse:collapse;font-size:13px">
        <tr><td style="padding:6px 12px;color:#555">Amount Received</td><td style="padding:6px 12px;text-align:right;font-weight:700;font-size:15px">₹ ${inr(d.amount)}</td></tr>
        ${d.againstBalance != null ? `<tr><td style="padding:6px 12px;color:#555">Invoice Balance</td><td style="padding:6px 12px;text-align:right">₹ ${inr(d.againstBalance)}</td></tr>` : ''}
      </table>
    </div>
    <div style="margin-top:8px;font-size:11px"><b>Amount in words:</b> INR ${escapeHtml(numberToWordsInr(d.amount))} Only</div>
    <div style="margin-top:36px;display:flex;justify-content:space-between;align-items:flex-end;font-size:11px;color:#555">
      ${d.qrUrl ? `<div style="text-align:center"><img src="${escapeHtml(d.qrUrl)}" style="width:96px;height:96px" alt="" onerror="this.style.display='none'"><div style="font-size:9px;color:#888">Scan to verify</div></div>` : '<div></div>'}
      <div style="text-align:center;min-width:200px">
        For <b>${escapeHtml(d.tenant.name)}</b>
        ${d.signatureUrl ? `<div><img src="${escapeHtml(d.signatureUrl)}" alt="" onerror="this.style.display='none'" style="max-height:50px;margin:6px 0"></div>` : '<div style="height:38px"></div>'}
        <div style="border-top:1px solid #94a3b8;padding-top:4px">Authorised Signatory</div>
      </div>
    </div>
  </div>`;
}

function soPartyCardHtml(p: PartyBlock): string {
  return `<div class="party-card">
    <h3>${escapeHtml(p.label)}</h3>
    <b>${escapeHtml(p.name)}</b><br>
    ${[p.address, p.city, p.state].filter(Boolean).map(escapeHtml).join(', ')}
    ${p.pincode ? `<br>Pin: ${escapeHtml(p.pincode)}` : ''}
    ${p.gstin ? `<br>GSTIN: ${escapeHtml(p.gstin)}` : ''}
    ${p.phone ? `<br>Mobile: ${escapeHtml(p.phone)}` : ''}
    ${p.email ? `<br>${escapeHtml(p.email)}` : ''}
  </div>`;
}

function salesOrderHtml(d: SalesOrderPrintInput): string {
  const totalQty = d.lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  // Admin-configurable optional columns (HSN / Unit) — same toggle as the tax
  // invoice so order/return/note prints share one column policy.
  const colCfg = getInvoiceColumns();
  type SoCol = { th: string; right?: boolean; left?: boolean; cell: (l: SalesOrderPrintLine, i: number) => string; foot?: string };
  const cols: SoCol[] = [
    { th: 'Sr.', cell: (_l, i) => String(i + 1) },
    { th: 'Item Name', left: true, cell: (l) => escapeHtml(l.description) },
    ...(colCfg.hsnCode ? [{ th: 'HSN/SAC', cell: (l: SalesOrderPrintLine) => escapeHtml(l.hsnCode ?? '') } as SoCol] : []),
    { th: 'Qty', right: true, cell: (l) => String(l.qty), foot: String(totalQty) },
    ...(colCfg.unit ? [{ th: 'Unit', cell: (l: SalesOrderPrintLine) => escapeHtml(l.unit ?? '') } as SoCol] : []),
    { th: 'Rate', right: true, cell: (l) => inr(l.rate) },
    { th: 'Amount', right: true, cell: (l) => inr(l.amount), foot: inr(d.subTotal) },
  ];
  const cAttr = (c: SoCol) => (c.right ? ' class="right"' : c.left ? ' style="text-align:left"' : '');
  const lineRows = d.lines.map((l, i) => `<tr>${cols.map((c) => `<td${cAttr(c)}>${c.cell(l, i)}</td>`).join('')}</tr>`).join('');
  const footRow = `<tr>${cols.map((c, idx) => (idx === 0 ? '<td><b>Total</b></td>' : `<td${c.right ? ' class="right"' : ''}><b>${c.foot ?? ''}</b></td>`)).join('')}</tr>`;
  const tax = (label: string, v: number) => (v > 0 ? `<tr class="line"><td>${label}</td><td class="right">${inr(v)}</td></tr>` : '');
  const bank = d.bank;
  // Pre-tax taxable value — shown as its own row so the "Taxable" box appears
  // below the line table (the field the user expects on the print).
  const taxable = d.taxableAmount != null ? d.taxableAmount : Math.max(0, d.subTotal - (d.tradeDiscount || 0));
  return `<div class="doc">
    <div style="text-align:center;font-size:15px;font-weight:700;letter-spacing:.1em;color:#1e3a8a;margin-bottom:6px">${escapeHtml(d.docTitle ?? 'SALES ORDER')}</div>
    <div class="header">
      <div>
        ${brandMarkHtml(d.tenant)}<h1>${escapeHtml(d.tenant.name)}</h1>
        <div class="meta-list">
          ${[d.tenant.address, d.tenant.city, d.tenant.state, d.tenant.pincode].filter(Boolean).map(escapeHtml).join(', ')}
          ${d.tenant.gstin ? `<br><b>GSTIN:</b> ${escapeHtml(d.tenant.gstin)}` : ''}
          ${d.tenant.phone ? `<br>${escapeHtml(d.tenant.phone)}` : ''}${d.tenant.email ? ` · ${escapeHtml(d.tenant.email)}` : ''}
        </div>
      </div>
      <div class="right meta-list">
        <b>Sales Order No:</b> ${escapeHtml(d.number)}<br>
        <b>Date:</b> ${escapeHtml(fmtDate(d.date))}
        ${d.buyerPoNumber ? `<br><b>Buyer P.O. No:</b> ${escapeHtml(d.buyerPoNumber)}` : ''}
        ${d.buyerPoDate ? `<br><b>Buyer P.O. Date:</b> ${escapeHtml(fmtDate(d.buyerPoDate))}` : ''}
        ${d.salesPerson ? `<br><b>Sales Person:</b> ${escapeHtml(d.salesPerson)}` : ''}
        ${d.dispatchThrough ? `<br><b>Dispatch Through:</b> ${escapeHtml(d.dispatchThrough)}` : ''}
        ${d.paymentTerms ? `<br><b>Payment Terms:</b> ${escapeHtml(d.paymentTerms)}` : ''}
      </div>
    </div>

    <div class="parties">
      ${soPartyCardHtml(d.billTo)}
      ${d.shipTo ? soPartyCardHtml(d.shipTo) : soPartyCardHtml({ ...d.billTo, label: 'Ship To (Consignee)' })}
    </div>

    <table class="lines">
      <thead><tr>${cols.map((c) => `<th${cAttr(c)}>${escapeHtml(c.th)}</th>`).join('')}</tr></thead>
      <tbody>${lineRows}</tbody>
      <tfoot>${footRow}</tfoot>
    </table>

    <div class="totals">
      <table>
        <tr class="line"><td>Sub Total</td><td class="right">${inr(d.subTotal)}</td></tr>
        ${tax('Trade Discount', d.tradeDiscount)}
        ${tax('Transport Charges', d.transportCharges)}
        <tr class="line"><td>Taxable Value</td><td class="right">${inr(taxable)}</td></tr>
        ${tax('CGST', d.cgstAmount)}
        ${tax('SGST', d.sgstAmount)}
        ${tax('IGST', d.igstAmount)}
        ${d.roundOff ? `<tr class="line"><td>Round Off</td><td class="right">${inr(d.roundOff)}</td></tr>` : ''}
        <tr class="grand"><td>Total</td><td class="right">₹ ${inr(d.totalAmount)}</td></tr>
      </table>
    </div>

    <div class="words"><b>Amount in words:</b> INR ${escapeHtml(numberToWordsInr(d.totalAmount))} Only</div>
    ${d.narration ? `<div class="words"><b>Narration:</b> ${escapeHtml(d.narration)}</div>` : ''}
    ${d.transporter || d.transportDetails ? `<div class="words"><b>Transporter:</b> ${escapeHtml([d.transporter, d.transportDetails].filter(Boolean).join(' · '))}</div>` : ''}

    <div class="footer" style="align-items:flex-end">
      <div>
        ${bank && (bank.accountNo || bank.bankName) ? `<b>Bank Details</b><br>
          ${bank.accountName ? `A/C Name: ${escapeHtml(bank.accountName)}<br>` : ''}
          ${bank.bankName ? `Bank: ${escapeHtml(bank.bankName)}<br>` : ''}
          ${bank.accountNo ? `A/C No: ${escapeHtml(bank.accountNo)}<br>` : ''}
          ${bank.ifsc ? `IFSC: ${escapeHtml(bank.ifsc)}<br>` : ''}
          ${bank.branch ? `Branch: ${escapeHtml(bank.branch)}` : ''}` : ''}
      </div>
      ${d.qrUrl ? `<div style="text-align:center"><img src="${escapeHtml(d.qrUrl)}" class="qr" alt="" onerror="this.style.display='none'"><div class="muted" style="font-size:9px">Scan to pay / verify</div></div>` : ''}
      <div class="right">
        For <b>${escapeHtml(d.tenant.name)}</b>
        ${d.signatureUrl ? `<div><img src="${escapeHtml(d.signatureUrl)}" alt="" onerror="this.style.display='none'" style="max-height:48px;margin-top:4px"></div>` : '<div style="height:22px"></div>'}
        <div class="signature">Authorised Signatory</div>
      </div>
    </div>
  </div>`;
}

/* -------------------------------------------------------------------- */
/*  Detailed GST Tax Invoice (HSN summary, IRN/Ack/E-Way, bank,         */
/*  uploadable signature image + QR).                                   */
/* -------------------------------------------------------------------- */

function taxPartyBlock(label: string, p: PartyBlock, pan?: string): string {
  return `<div class="party-card">
    <h3>${escapeHtml(label)}</h3>
    <table style="width:100%;font-size:10.5px;border:none">
      <tr><td style="border:none;padding:1px 0;width:90px">Name</td><td style="border:none;padding:1px 0"><b>${escapeHtml(p.name)}</b></td></tr>
      <tr><td style="border:none;padding:1px 0;vertical-align:top">Address</td><td style="border:none;padding:1px 0">${escapeHtml(p.address ?? '')}</td></tr>
      <tr><td style="border:none;padding:1px 0">City</td><td style="border:none;padding:1px 0">${escapeHtml(p.city ?? '')}</td></tr>
      <tr><td style="border:none;padding:1px 0">State</td><td style="border:none;padding:1px 0">${escapeHtml(p.state ?? '')}${p.pincode ? ` &nbsp; Pin: ${escapeHtml(p.pincode)}` : ''}</td></tr>
      ${p.email ? `<tr><td style="border:none;padding:1px 0">Email</td><td style="border:none;padding:1px 0">${escapeHtml(p.email)}</td></tr>` : ''}
      ${p.phone ? `<tr><td style="border:none;padding:1px 0">Phone</td><td style="border:none;padding:1px 0">${escapeHtml(p.phone)}</td></tr>` : ''}
      ${p.gstin ? `<tr><td style="border:none;padding:1px 0">GSTIN</td><td style="border:none;padding:1px 0"><b>${escapeHtml(p.gstin)}</b></td></tr>` : ''}
      ${pan ? `<tr><td style="border:none;padding:1px 0">PAN</td><td style="border:none;padding:1px 0">${escapeHtml(pan)}</td></tr>` : ''}
    </table>
  </div>`;
}

function taxInvoiceHtml(d: InvoicePrintInput): string {
  const interstate = d.igstAmount > 0;
  const billTo = d.parties[0];
  const shipTo = d.parties[1] ?? d.parties[0];
  const totalQty = d.lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const totalPacks = d.lines.reduce((s, l) => s + (Number(l.noOfPacks) || 0), 0);
  // Admin-configurable column set (Settings → Print Formats). Mandatory cols
  // (Sr/Description/Qty/Rate/Taxable) always render; the optional ones honour
  // the per-tenant toggle so different customers can get different layouts.
  const colCfg = getInvoiceColumns();
  type Col = { th: string; right?: boolean; left?: boolean; cell: (l: InvoiceLine, i: number) => string; foot?: string };
  const cols: Col[] = [
    { th: 'Sr', cell: (_l, i) => String(i + 1) },
    { th: 'Description', left: true, cell: (l) => escapeHtml(l.description) },
    ...(colCfg.remarks ? [{ th: 'Remarks', cell: (l: InvoiceLine) => escapeHtml(l.remarks ?? '') } as Col] : []),
    ...(colCfg.hsnCode ? [{ th: 'HSN/SAC', cell: (l: InvoiceLine) => escapeHtml(l.hsnCode ?? '') } as Col] : []),
    { th: 'Qty', right: true, cell: (l) => String(l.qty), foot: String(totalQty) },
    ...(colCfg.unit ? [{ th: 'Unit', cell: (l: InvoiceLine) => escapeHtml(l.unit ?? '') } as Col] : []),
    ...(colCfg.packs ? [{ th: 'Packs', right: true, cell: (l: InvoiceLine) => String(l.noOfPacks ?? ''), foot: String(totalPacks || '') } as Col] : []),
    ...(colCfg.packType ? [{ th: 'Pack', cell: (l: InvoiceLine) => escapeHtml(l.packType ?? '') } as Col] : []),
    { th: 'Rate', right: true, cell: (l) => inr(l.rate) },
    ...(colCfg.tradeDiscount ? [{ th: 'Trade Dis', right: true, cell: (l: InvoiceLine) => (l.tradeDiscount ? inr(l.tradeDiscount) : '') } as Col] : []),
    { th: 'Taxable', right: true, cell: (l) => inr(l.taxable ?? (l.qty * l.rate - (l.tradeDiscount ?? 0))), foot: inr(d.taxableAmount) },
  ];
  const cAttr = (c: Col) => (c.right ? ' class="right"' : c.left ? ' style="text-align:left"' : '');
  const lineRows = d.lines.map((l, i) => `<tr>${cols.map((c) => `<td${cAttr(c)}>${c.cell(l, i)}</td>`).join('')}</tr>`).join('');
  const footRow = `<tr>${cols.map((c, idx) => (idx === 0 ? '<td><b>Total</b></td>' : `<td${c.right ? ' class="right"' : ''}><b>${c.foot ?? ''}</b></td>`)).join('')}</tr>`;
  const taxRow = (label: string, v: number) => (v ? `<tr><td>${label}</td><td class="right">${inr(v)}</td></tr>` : '');
  const hsnRows = (d.hsnSummary ?? []).map((h) => `<tr>
      <td>${escapeHtml(h.hsn)}</td>
      <td class="right">${inr(h.taxable)}</td>
      <td class="right">${h.cgstRate ? h.cgstRate + '%' : ''}</td><td class="right">${h.cgst ? inr(h.cgst) : ''}</td>
      <td class="right">${h.sgstRate ? h.sgstRate + '%' : ''}</td><td class="right">${h.sgst ? inr(h.sgst) : ''}</td>
      <td class="right">${h.igstRate ? h.igstRate + '%' : ''}</td><td class="right">${h.igst ? inr(h.igst) : ''}</td>
    </tr>`).join('');
  const bank = d.bank;

  return `<div class="doc">
    <div class="header">
      <div>
        ${d.tenant.gstin ? `<div class="meta-list"><b>GSTIN:</b> ${escapeHtml(d.tenant.gstin)}</div>` : ''}
        ${d.tenant.pan ? `<div class="meta-list"><b>PAN:</b> ${escapeHtml(d.tenant.pan)}</div>` : ''}
        ${brandMarkHtml(d.tenant)}<h1>${escapeHtml(d.tenant.name)}</h1>
        <div class="meta-list">${[d.tenant.address, d.tenant.city, d.tenant.state, d.tenant.pincode].filter(Boolean).map(escapeHtml).join(', ')}</div>
      </div>
      <div class="right"><div class="doctype">${escapeHtml(d.docType || 'TAX INVOICE')}</div></div>
    </div>

    <div class="parties" style="grid-template-columns:1fr 1fr;margin:8px 0">
      <div class="meta-list">
        <b>Invoice No:</b> ${escapeHtml(d.number)}<br>
        <b>Date:</b> ${escapeHtml(fmtDate(d.date))}<br>
        ${d.challanNumber ? `<b>Challan No:</b> ${escapeHtml(d.challanNumber)}<br>` : ''}
        ${d.placeOfSupply ? `<b>Place of Supply:</b> ${escapeHtml(d.placeOfSupply)}` : ''}
      </div>
      <div class="right meta-list">
        ${d.paymentTerms ? `<b>Payment Terms:</b> ${escapeHtml(d.paymentTerms)}<br>` : ''}
        ${d.dueDate ? `<b>Due On:</b> ${escapeHtml(fmtDate(d.dueDate))}` : ''}
      </div>
    </div>

    <div class="parties">
      ${taxPartyBlock('Details Of Receiver (Billed To)', billTo, d.panNumber)}
      ${taxPartyBlock('Details Of Consignee (Shipped To)', shipTo, d.panNumber)}
    </div>

    <table class="lines" style="font-size:10.5px">
      <thead><tr>${cols.map((c) => `<th${cAttr(c)}>${escapeHtml(c.th)}</th>`).join('')}</tr></thead>
      <tbody>${lineRows}</tbody>
      <tfoot>${footRow}</tfoot>
    </table>

    <div class="row" style="margin-top:8px">
      <div style="flex:1">
        <div class="words"><b>Invoice Value (In Words):</b> INR ${escapeHtml(numberToWordsInr(d.totalAmount))} Only</div>
        ${d.irn ? `<div class="meta-list" style="margin-top:6px;word-break:break-all"><b>IRN:</b> ${escapeHtml(d.irn)}</div>` : ''}
        ${d.ackNo ? `<div class="meta-list"><b>Ack No:</b> ${escapeHtml(d.ackNo)}${d.ackDate ? ` &nbsp; <b>Ack Date:</b> ${escapeHtml(d.ackDate)}` : ''}</div>` : ''}
        ${d.ewayBillNo ? `<div class="meta-list"><b>E-Way Bill No:</b> ${escapeHtml(d.ewayBillNo)}</div>` : ''}
      </div>
      <div class="totals" style="margin-top:0">
        <table>
          <tr><td>Taxable Value</td><td class="right">${inr(d.taxableAmount)}</td></tr>
          ${taxRow('Trade Discount', d.totalDiscount)}
          ${d.couponDiscount ? `<tr><td>Coupon${d.couponCode ? ` (${escapeHtml(d.couponCode)})` : ''}</td><td class="right">− ${inr(d.couponDiscount)}</td></tr>` : ''}
          ${taxRow('Freight Charges', d.freightCharges ?? 0)}
          ${taxRow('CGST', d.cgstAmount)}
          ${taxRow('SGST', d.sgstAmount)}
          ${taxRow('IGST', d.igstAmount)}
          ${d.roundOff ? `<tr><td>Round Off</td><td class="right">${inr(d.roundOff)}</td></tr>` : ''}
          <tr class="grand"><td>Invoice Total</td><td class="right">₹ ${inr(d.totalAmount)}</td></tr>
        </table>
      </div>
    </div>

    ${hsnRows ? `<table class="lines" style="margin-top:10px;font-size:10px">
      <thead>
        <tr><th rowspan="2">HSN/SAC</th><th rowspan="2" class="right">Taxable Value</th><th colspan="2">Central Tax</th><th colspan="2">State Tax</th><th colspan="2">Integrated Tax</th></tr>
        <tr><th class="right">Rate</th><th class="right">Amount</th><th class="right">Rate</th><th class="right">Amount</th><th class="right">Rate</th><th class="right">Amount</th></tr>
      </thead>
      <tbody>${hsnRows}</tbody>
    </table>` : ''}

    <div class="footer" style="margin-top:6px;align-items:flex-end">
      <div>
        ${bank && (bank.accountNo || bank.name) ? `<b>Our Bank Details</b><br>
          ${bank.name ? `Bank: ${escapeHtml(bank.name)}<br>` : ''}
          ${bank.accountName ? `A/C Name: ${escapeHtml(bank.accountName)}<br>` : ''}
          ${bank.accountNo ? `A/C No: ${escapeHtml(bank.accountNo)}<br>` : ''}
          ${bank.ifsc ? `IFSC: ${escapeHtml(bank.ifsc)}<br>` : ''}
          ${bank.branch ? `Branch: ${escapeHtml(bank.branch)}` : ''}` : ''}
      </div>
      ${d.qrUrl ? `<div style="text-align:center"><img src="${escapeHtml(d.qrUrl)}" class="qr" alt="" onerror="this.style.display='none'"><div class="muted" style="font-size:9px">Scan to pay / verify</div></div>` : ''}
      <div class="right">
        For <b>${escapeHtml(d.tenant.name)}</b>
        ${d.signatureUrl ? `<div><img src="${escapeHtml(d.signatureUrl)}" alt="" onerror="this.style.display='none'" style="max-height:48px;margin-top:4px"></div>` : '<div style="height:22px"></div>'}
        <div class="signature">Authorised Signatory</div>
      </div>
    </div>
  </div>`;
}
